/**
 * ============================================
 * ENVIRONMENT PANEL
 * ============================================
 * 
 * Shows for each environment:
 *   - Remote config endpoints (all versions in a list)
 *   - Service status (Nakama, CockroachDB, EC2, ALB, ASG, Redis, Lambda)
 *   - Summary stats (S3 Objects + Total Players + Avg Session Length)
 *   - CockroachDB section (CCU Graph + Platform breakdown)
 */

import type { EnvironmentConfig } from '@/config';
import { BACKEND_URL } from '@/config';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { ServiceStatusGrid } from './ServiceStatusGrid';
import { CockroachDBSection } from './CockroachDBSection';
import { UserGrowthChart } from './UserGrowthChart';
import { useEnvironmentData, useAlertMonitor, useCCU, useCCUAlarm } from '@/hooks';
import { useAlerts } from '@/context/AlertContext';
import { Globe, AlertTriangle, HardDrive, Users, Clock } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

// When the multi-org failover view is rendered above (which already shows
// service health + remote-config versions for both orgs across the priority
// chain), hide the per-env Service Status Card here to avoid duplication.
// CockroachDB section and UserGrowthChart still render -- those are
// per-env-only data not surfaced in the multi-org view.
const PREVIEW_MULTI_ORG = import.meta.env.VITE_PREVIEW_MULTI_ORG === 'true';

interface EnvironmentPanelProps {
  environment: EnvironmentConfig;
  onOrgName?: (name: string) => void;
}

function formatCount(count: number): string {
  if (count >= 1000000) {
    const value = count / 1000000;
    return value % 1 === 0 ? `${value}M` : `${value.toFixed(1)}M`;
  }
  if (count >= 1000) {
    const value = count / 1000;
    return value % 1 === 0 ? `${value}K` : `${value.toFixed(1)}K`;
  }
  return count.toString();
}

export function EnvironmentPanel({ environment, onOrgName }: EnvironmentPanelProps) {
  const { 
    servicesHealth, 
    overallStatus, 
    statusColor,
    healthyCount,
    totalCount,
    infrastructure,
    totalPlayers,
    platformBreakdown,
    platformCombinations,
    remoteConfig,
    clusterName,
    awsOrgName,
    isLoading,
  } = useEnvironmentData(environment.id);
  
  // Get CCU data
  const { currentCCU, ccuHistory, duration: ccuDuration, refetch: refetchCCU, error: ccuError } = useCCU('1h');
  
  // Get Avg Session Length from analytics
  const [avgSessionLength, setAvgSessionLength] = useState<number>(0);
  
  useEffect(() => {
    // Fetch avg session length from PS5/Domo analytics endpoint
    const fetchAvgSession = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/v1/ps5/analytics`);
        if (response.ok && response.status !== 202) {
          const data = await response.json();
          if (data.summary?.avgSessionMinutes !== undefined) {
            setAvgSessionLength(data.summary.avgSessionMinutes || 0);
          }
        }
      } catch (err) {
        console.error('Failed to fetch avg session length:', err);
      }
    };
    fetchAvgSession();

    // Refresh every 5 minutes
    const interval = setInterval(fetchAvgSession, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Report org name to parent
  useEffect(() => {
    if (awsOrgName && onOrgName) onOrgName(awsOrgName);
  }, [awsOrgName, onOrgName]);

  // CCU alarm handling - stored locally per device
  const { shouldAlarm: ccuShouldAlarm, thresholdCrossed, acknowledgeAlarm: acknowledgeCCUAlarm } = useCCUAlarm(currentCCU);
  const { addAlert } = useAlerts();
  const ccuAlarmTriggeredRef = useRef<number | null>(null);
  
  // Trigger CCU alarm when threshold is crossed (only if CCU data is available)
  useEffect(() => {
    // Skip if CCU data is not available
    if (ccuError || ccuHistory.length === 0) return;

    if (ccuShouldAlarm && thresholdCrossed && thresholdCrossed !== ccuAlarmTriggeredRef.current) {
      ccuAlarmTriggeredRef.current = thresholdCrossed;

      // Create a CCU alert
      addAlert({
        id: `ccu-threshold-${thresholdCrossed}-${Date.now()}`,
        type: 'ccu_threshold',
        severity: 'critical',
        title: `🎉 CCU Milestone: ${thresholdCrossed.toLocaleString()} Users!`,
        message: `Concurrent users reached ${currentCCU.toLocaleString()}! (Threshold: ${thresholdCrossed.toLocaleString()})`,
        environmentId: environment.id,
        environmentName: environment.name,
        timestamp: new Date(),
        acknowledged: false,
      }, true); // bypass grace period for CCU alarms

      // Auto-acknowledge after triggering to update local storage
      acknowledgeCCUAlarm();
    }
  }, [ccuShouldAlarm, thresholdCrossed, currentCCU, environment.id, addAlert, acknowledgeCCUAlarm, ccuError, ccuHistory.length]);
  
  // Alert monitoring - checks service health and triggers alerts
  useAlertMonitor({
    environment,
    servicesHealth,
  });

  // Check if Lambda exists (count > 0)
  const lambdaExists = infrastructure.lambdaFunctions > 0;

  return (
    <div className="space-y-6">
      {/* Service Status Card -- HIDDEN when multi-org failover view is shown
          above (it already covers service health + remote-config versions
          for every env in the priority chain). Flip the flag to false to
          restore the legacy single-org view here. */}
      {!PREVIEW_MULTI_ORG && (
      <Card>
        <CardHeader className="pb-3">
          <div className="space-y-2">
            {/* First Row: Environment Name + Health Status */}
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <span
                  className={`h-3 w-3 rounded-full ${
                    overallStatus === 'healthy' ? '' :
                    overallStatus === 'warning' ? 'animate-pulse' :
                    overallStatus === 'critical' ? 'animate-pulse' : ''
                  }`}
                  style={{ backgroundColor: statusColor }}
                />
                {environment.name} Environment
              </CardTitle>

              <div className="flex items-center gap-2">
                <span
                  className={`h-3 w-3 rounded-full ${
                    overallStatus === 'warning' || overallStatus === 'critical'
                      ? 'animate-pulse' : ''
                  }`}
                  style={{ backgroundColor: statusColor }}
                />
                <span className="text-sm">
                  {isLoading ? 'Checking...' : `${healthyCount}/${totalCount} healthy`}
                </span>
              </div>
            </div>

            {/* Second Row: Remote Config Versions */}
            <div className="flex items-start gap-2">
              <Globe className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex flex-wrap gap-2">
                {isLoading ? (
                  <span className="text-xs text-muted-foreground">Loading config...</span>
                ) : remoteConfig.length > 0 ? (
                  remoteConfig.map((ep, index) => (
                    <span
                      key={index}
                      className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                        ep.forceUpdate
                          ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                          : 'bg-muted/50 text-muted-foreground'
                      }`}
                    >
                      {ep.forceUpdate && <AlertTriangle className="h-3 w-3" />}
                      <span className="font-semibold">v{ep.version}</span>
                      <span className="opacity-70">→</span>
                      <span className="truncate max-w-[200px]">{ep.url}</span>
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-muted-foreground">No config available</span>
                )}
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Service Status Grid */}
          <ServiceStatusGrid
            servicesHealth={servicesHealth}
            lambdaExists={lambdaExists}
            isLoading={isLoading}
          />

          <div className="border-t border-border" />

          {/* Summary Stats Row: Total Players + S3 Objects + Avg Session */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Total Players - Green/Teal gradient */}
            <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-br from-green-500/10 to-teal-500/10 dark:from-green-600/20 dark:to-teal-600/20 border border-green-400/40 dark:border-green-500/30">
              <div className="absolute top-2 right-2 opacity-10 dark:opacity-20">
                <Users className="h-10 w-10 sm:h-12 sm:w-12 text-green-500 dark:text-green-400" />
              </div>
              <p className="text-xs text-green-600 dark:text-green-300 mb-1 flex items-center gap-1">
                <Users className="h-3 w-3" />
                Total Players
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400">{formatCount(totalPlayers)}</p>
            </div>

            {/* S3 Objects - Blue/Purple gradient */}
            <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-br from-blue-500/10 to-purple-500/10 dark:from-blue-600/20 dark:to-purple-600/20 border border-blue-400/40 dark:border-blue-500/30">
              <div className="absolute top-2 right-2 opacity-10 dark:opacity-20">
                <HardDrive className="h-10 w-10 sm:h-12 sm:w-12 text-blue-500 dark:text-blue-400" />
              </div>
              <p className="text-xs text-blue-600 dark:text-blue-300 mb-1 flex items-center gap-1">
                <HardDrive className="h-3 w-3" />
                Level Uploads
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400">{infrastructure.s3Objects.toLocaleString()}</p>
            </div>

            {/* Avg Session Length - Orange/Yellow gradient */}
            <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-br from-orange-500/10 to-yellow-500/10 dark:from-orange-600/20 dark:to-yellow-600/20 border border-orange-400/40 dark:border-orange-500/30">
              <div className="absolute top-2 right-2 opacity-10 dark:opacity-20">
                <Clock className="h-10 w-10 sm:h-12 sm:w-12 text-orange-500 dark:text-orange-400" />
              </div>
              <p className="text-xs text-orange-600 dark:text-orange-300 mb-1 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Avg. Session Length
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-orange-600 dark:text-orange-400">{avgSessionLength} <span className="text-base sm:text-lg">min</span></p>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* CockroachDB Section (CCU Graph + Platform breakdown) */}
      <CockroachDBSection
        clusterName={clusterName}
        totalPlayers={totalPlayers}
        platformBreakdown={platformBreakdown}
        platformCombinations={platformCombinations}
        ccuData={ccuHistory}
        ccuDuration={ccuDuration}
        onCCUDurationChange={refetchCCU}
        currentCCU={currentCCU}
        isLoading={isLoading}
      />

      {/* User Growth Chart */}
      <UserGrowthChart environmentId={environment.id} />
    </div>
  );
}
